
import { ClickRhythmTracker } from './click-rhythm';

export type AutomationReason = 'webdriver' | 'headless' | 'framework' | 'synthetic_input' | 'click_pattern' | 'ai_agent';

let current: AutomationReason | null = null;
const listeners = new Set<() => void>();

export function automationReason(): AutomationReason | null {
  return current;
}

export function subscribeAutomation(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function flagAutomation(reason: AutomationReason): void {
  if (current) return;
  current = reason;
  for (const listener of listeners) listener();
}

const HEADLESS_USER_AGENT = /HeadlessChrome|PhantomJS|SlimerJS/i;
const ELECTRON_USER_AGENT = /Electron\//i;

const AUTOMATION_GLOBALS = [
  '__playwright__binding__',
  '__pwInitScripts',
  '__puppeteer_evaluation_script__',
  '_phantom',
  'callPhantom',
  '__nightmare',
  'domAutomation',
  'domAutomationController',
  '_selenium',
  'calledSelenium',
  '_Selenium_IDE_Recorder',
  '__selenium_unwrapped',
  '__selenium_evaluate',
  '__webdriver_evaluate',
  '__webdriver_script_fn',
  '__webdriver_script_func',
  '__driver_evaluate',
  '__driver_unwrapped',
  '__fxdriver_evaluate',
  '__fxdriver_unwrapped',
  '__lastWatirAlert',
  'Cypress',
];

export function detectAutomationEnvironment(win: Window = window): AutomationReason | null {
  const nav = win.navigator;
  if (nav.webdriver === true) return 'webdriver';
  if (HEADLESS_USER_AGENT.test(nav.userAgent)) return 'headless';
  if (!import.meta.env.DEV && ELECTRON_USER_AGENT.test(nav.userAgent)) return 'headless';
  const record = win as unknown as Record<string, unknown>;
  if (AUTOMATION_GLOBALS.some((name) => name in record)) return 'framework';
  if (Object.keys(win.document).some((key) => /^\$?cdc_/.test(key))) return 'framework';
  const root = win.document.documentElement;
  if (['webdriver', 'selenium', 'driver'].some((attribute) => root.hasAttribute(attribute))) return 'framework';
  return null;
}


const GAME_CONTROL = 'button, [role="button"], a[href]:not([download])';

function onUntrustedInput(event: Event): void {
  if (event.isTrusted) return;
  const target = event.target;
  if (!(target instanceof Element) || !target.closest(GAME_CONTROL)) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  flagAutomation('synthetic_input');
}

const rhythm = new ClickRhythmTracker();


function onTrustedClick(event: MouseEvent): void {
  if (!event.isTrusted || event.detail === 0) return;
  if (!rhythm.record(event.timeStamp)) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  flagAutomation('click_pattern');
}


export function watchAutomation(win: Window = window): void {
  const check = () => {
    const reason = detectAutomationEnvironment(win);
    if (reason) flagAutomation(reason);
  };
  check();
  win.addEventListener('load', check, { once: true });
  win.setTimeout(check, 3000);
  win.setInterval(check, 30_000);
  for (const type of ['click', 'pointerdown', 'mousedown'] as const) {
    win.addEventListener(type, onUntrustedInput, { capture: true });
  }
  win.addEventListener('click', onTrustedClick, { capture: true });
}
